import React, { useState, useEffect } from 'react';
import { Calendar } from '@/components/ui/calendar';

export interface DateRange {
  from: Date | undefined;
  to: Date | undefined;
}

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  disabled?: boolean;
  className?: string;
  onDaysCalculated?: (days: number) => void;
  // Keep these for compatibility but don't use them for now
  businessId?: string;
  minDate?: Date;
  maxDate?: Date;
  onAvailabilityLoad?: (data: any[]) => void;
  autoUpdateDaysField?: string;
  onFieldUpdate?: (fieldName: string, value: number) => void;
  availabilityConfig?: any;
}

export function DateRangePicker({
  value,
  onChange,
  disabled = false,
  className,
  onDaysCalculated,
  minDate,
  maxDate,
}: DateRangePickerProps) {
  // Add a small delay to ensure proper calendar initialization
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Small delay to ensure DOM is ready and Calendar component is properly initialized
    const timer = setTimeout(() => setIsReady(true), 50);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className={className}>
      <div className="p-4 border rounded-lg bg-white">
        <h3 className="text-lg font-semibold mb-4">Select Your Dates</h3>
        {isReady ? (
          <Calendar
            mode="range"
            selected={value}
            onSelect={(range) => {
              if (range) {
                const newRange = { from: range.from, to: range.to };
                onChange(newRange);
                
                // Calculate days if both dates selected
                if (range.from && range.to) {
                  const days = Math.ceil((range.to.getTime() - range.from.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                  onDaysCalculated?.(days);
                }
              }
            }}
            disabled={disabled}
            fromDate={minDate}
            toDate={maxDate}
            className="mx-auto"
            // Add key to force re-render and ensure proper initialization
            key="date-range-calendar"
          />
        ) : (
          <div className="h-64 flex items-center justify-center">
            <div className="animate-pulse text-sm text-gray-500">Loading calendar...</div>
          </div>
        )}
        
        {/* Show selected range */}
        {value.from && value.to && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm font-medium text-blue-900">
              Selected: {value.from.toLocaleDateString()} - {value.to.toLocaleDateString()}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}